import clsx from 'clsx';
import {
  OpUnitType,
} from 'dayjs';
import map from 'lodash/map';
import reject from 'lodash/reject';
import capitalize from 'lodash/capitalize';
import isNil from 'lodash/isNil';
import {
  CreateAppointmentRequestDTO,
  BlockedTimeSlotDTO,
  ServiceFacilityDTO,
  DoctorsAppointmentSummaryDTO,
  ProviderAvailabilityDTO,
  PatientDTO,
  UserDTO,
  AppointmentDTO,
  ProviderContextDTO,
} from 'dtos';
import {
  RecurrenceTypes,
} from 'dtos/recurrenceTypes';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  getColorByName,
} from 'utils/colors';
import {
  formatDateOnly,
  formatISODateTime,
} from 'utils/date';
import {
  CreateEventFormData,
  CalendarEvent,
  CalendarViewEvent,
  CalendarTimelineEvent,
  EventType,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import {
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';

type DefaultAppointmentDataProps = {
  defaultStartDateTime: Dayjs;
  defaultEndDateTime?: Dayjs | null;
  doctor?: UserDTO | null;
  serviceFacility?: ServiceFacilityDTO;
  patient?: Partial<PatientDTO>;
  reason?: string;
};

type DefaultEventFormDataProps = {
  defaultStartDateTime: Dayjs;
  defaultDurationInMinutes?: number;
  doctor?: UserDTO | null;
  serviceFacility?: ServiceFacilityDTO;
  patient?: Partial<PatientDTO>;
  reason?: string;
};

type AppointmentParams = Omit<CreateEventFormData, 'eventDateTime' | 'doctor' | 'recurrence'> & { start: Dayjs };

type RecurringEventParams = Pick<CreateEventFormData, 'recurrence' | 'serviceFacility'> &
  { start: Dayjs; recurrenceType: RecurrenceTypes };

export const DEFAULT_APPOINTMENT_DURATION: number = 15;

export type CalendarViewType = 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';

export const DATE_UNIT_BY_VIEW_TYPE: Record<CalendarViewType, Partial<OpUnitType>> = Object.freeze({
  Daily: 'day',
  Weekly: 'week',
  Monthly: 'month',
  Yearly: 'year',
});

export const eventRecurrenceMap: Record<string, string> = {
  daily: 'Day',
  weekly: 'Week',
};

type RequestPayloadType = CreateAppointmentRequestDTO
  & BlockedTimeSlotDTO
  & ProviderAvailabilityDTO;

export function normalizeDailyBreakdown(
  summary: DoctorsAppointmentSummaryDTO[],
): CalendarTimelineEvent[] {
  const events: CalendarTimelineEvent[] = [];
  summary.forEach(({ userId = 0, dailyAppointmentBreakdowns: breakdowns }) => {
    breakdowns?.forEach(({ serviceFacilityIds, date, appointmentTypesCount: counts }) => {
      serviceFacilityIds?.forEach((serviceFacilityId) => {
        events.push({
          userId,
          count: 0,
          typeOfVisitDescription: 'office',
          serviceFacilityId,
          isTimeAvailability: true,
          allDay: true,
          start: dayjs(date).startOf('day').toDate(),
          end: dayjs(date).endOf('day').toDate(),
          resource: userId,
          title: 'Office',
        });
      });
      counts?.forEach(({ count = 0, typeOfVisitDescription }) => {
        if (typeOfVisitDescription !== 'Time Available') {
          events.push({
            userId,
            count,
            isTimeBlock: typeOfVisitDescription === 'Time Blocked',
            typeOfVisitDescription: typeOfVisitDescription ?? '',
            // mobiscroll event properties below
            allDay: true,
            start: dayjs(date).startOf('day').toDate(),
            end: dayjs(date).endOf('day').toDate(),
            resource: userId,
            title: `${count} - ${typeOfVisitDescription}`,
          });
        }
      });
    });
  });

  return events;
}

function getAppointmentPayload({
  insurance,
  start,
  patient,
  reason,
  typeOfVisitDescription,
  canBeRescheduledSooner,
  serviceFacility,
  referringProvider,
  isOutOfPocket,
}: AppointmentParams): CreateAppointmentRequestDTO {
  return {
    insuranceId: isNilOrZero(insurance?.insuranceId) ? null : insurance?.insuranceId,
    startDateTime: formatISODateTime(start),
    patientId: patient?.patientId ?? 0,
    reasonForVisit: reason,
    typeOfVisitDescription,
    canBeRescheduledSooner,
    serviceFacilityId: serviceFacility?.serviceFacilityId,
    referringProvider: referringProvider ?? undefined,
    isOutOfPocket,
  };
}

function getBaseRecurringEventPayload({
  recurrenceType: type,
  start,
}: Partial<RecurringEventParams>): Partial<ProviderAvailabilityDTO> {
  return {
    recurrenceType: type,
    startDateTime: formatISODateTime(start ?? ''),
  };
}

function getRecurrenceDataForEventPayload({
  recurrence,
  recurrenceType,
}: Partial<RecurringEventParams>): Partial<ProviderAvailabilityDTO> {
  return /^single$/i.test(recurrenceType ?? '') ? {} : {
    interval: Number(recurrence?.interval ?? 0),
    ...(recurrenceType === 'Weekly' ? { repeatsOn: map(recurrence?.repeatsOn, 'id') } : {}),
    ...(recurrence?.endsOn ? { endsOn: formatISODateTime(dayjs(recurrence?.endsOn).endOf('day')) } : {}),
    ...(recurrence?.count ? { count: Number(recurrence?.count) } : {}),
  };
}

function getRecurringEventPayload(
  data: Partial<RecurringEventParams>,
): Partial<ProviderAvailabilityDTO> {
  const { recurrenceType: type, start, recurrence } = data;
  const recurrenceType = capitalize(
    recurrence?.repeatType?.id?.toString() ?? 'single',
  ) as RecurrenceTypes;
  const payloadParams = {
    ...data,
    recurrenceType,
  };
  return {
    recurrenceType: type,
    startDateTime: formatISODateTime(start ?? ''),
    ...getBaseRecurringEventPayload(payloadParams),
    ...getRecurrenceDataForEventPayload(payloadParams),
  };
}

function getAvailabilityPayload(
  params: Partial<RecurringEventParams>,
): ProviderAvailabilityDTO {
  return {
    ...getRecurringEventPayload(params),
    serviceFacilityId: params.serviceFacility?.serviceFacilityId,
  };
}

function getTimeBlockPayload(
  params: Pick<AppointmentParams, 'reason'> & Partial<RecurringEventParams>,
): BlockedTimeSlotDTO {
  const { reason } = params;
  return {
    ...getRecurringEventPayload(params),
    reasonForBlock: reason,
  };
}

const payloadFunctionsByType = Object.freeze([
  getAppointmentPayload,
  getTimeBlockPayload,
  getAvailabilityPayload,
]);

export function serializeProviderDates(
  seenDate?: string | null,
): string | null {
  return notEmptyString(seenDate) && dayjs(seenDate).isValid() ? formatDateOnly(seenDate ?? '') : null;
}

export function serializeReferringProvider(
  referringProvider?: ProviderContextDTO | null,
): ProviderContextDTO | undefined {
  return isNil(referringProvider?.providerEntry) ? undefined : {
    ...referringProvider,
    firstSeenOn: serializeProviderDates(referringProvider?.firstSeenOn),
    lastSeenOn: serializeProviderDates(referringProvider?.lastSeenOn),
  };
}

export function serializeAppointmentFormData(
  {
    doctor,
    eventDateTime,
    referringProvider,
    ...formData
  }: CreateEventFormData,
  appointmentType: number,
): RequestPayloadType {
  const {
    startTime,
    durationInMinutes,
  } = eventDateTime;

  return {
    ...(payloadFunctionsByType[appointmentType]({
      ...formData,
      start: startTime,
    })),
    userId: doctor?.userId ?? 0,
    referringProvider: serializeReferringProvider(referringProvider),
    durationInMinutes,
  };
}

export function getDefaultEventFormData({
  defaultStartDateTime,
  defaultDurationInMinutes = DEFAULT_APPOINTMENT_DURATION,
  doctor,
  serviceFacility,
  patient,
  reason,
}: DefaultEventFormDataProps): CreateEventFormData {
  return {
    patient: patient ?? { dateOfBirth: '' },
    doctor,
    eventDateTime: {
      startTime: defaultStartDateTime,
      durationInMinutes: defaultDurationInMinutes,
    },
    typeOfVisitDescription: '',
    reason: reason ?? '',
    canBeRescheduledSooner: false,
    serviceFacility,
  };
}

export function getAppointmentPlaceholderData({
  defaultStartDateTime,
  defaultEndDateTime,
}: DefaultAppointmentDataProps) {
  return {
    title: '*',
    start: defaultStartDateTime.toDate(),
    end: isNil(defaultEndDateTime)
      ? defaultStartDateTime.add(DEFAULT_APPOINTMENT_DURATION, 'minute').toDate()
      : defaultEndDateTime.toDate(),
    color: getColorByName('gray-300'),
    placeholder: true,
  };
}

const titlesByType: Record<EventType, (e: CalendarEvent) => string> = Object.freeze({
  appointment: ({ patient }: CalendarEvent) => getDisplayName(patient?.formattedNames, '--'),
  timeBlock: ({ reason }: CalendarEvent) => reason ?? '--',
  availability: ({ serviceFacility }: CalendarEvent) => serviceFacility?.nickname ?? '--',
});

export const eventTypeTitles: Readonly<Record<EventType, string>> = Object.freeze({
  appointment: 'Appointment',
  timeBlock: 'Time Block',
  availability: 'Availability',
});

function getEventTitle({ typeName, ...event }: CalendarEvent) {
  return titlesByType[typeName ?? 'appointment'](event);
}

export function isCancelledOrNoShow(appointment: AppointmentDTO) {
  return /cancelled|no show/i.test(appointment.patientAppointmentStatus ?? '');
}

export function convertToCalendarViewEvents(
  calendarEvents: CalendarEvent[],
  filteredIdSet: Set<string>,
  currentEventId?: number,
): CalendarViewEvent[] {
  return ((reject(
    calendarEvents,
    ({ isTimeAvailability, isDefault }) => isTimeAvailability && isDefault,
  ) ?? []) as CalendarEvent[]).map(
    (event) => {
      const {
        id,
        objectId,
        userId,
        isTimeBlock,
        start,
        end,
        patient,
        patientAlerts,
        color,
        cssClass,
        recurrence,
        isTimeAvailability,
        serviceFacility,
      } = event;
      const editable = (isNil(recurrence) && isNil(currentEventId))
        || (objectId === currentEventId && !isNil(currentEventId));

      const isCancelledOrNoShowEvent = isCancelledOrNoShow(event);
      const isFilteredOut = (!filteredIdSet?.has(id) && !(isTimeBlock || isTimeAvailability));

      return {
        id,
        patientId: patient?.patientId ?? 0,
        patientAlerts,
        objectId,
        isTimeBlock: Boolean(isTimeBlock),
        isTimeAvailability: Boolean(isTimeAvailability),
        resource: userId,
        start: (start as Dayjs).toDate(),
        end: (end as Dayjs).toDate(),
        title: getEventTitle(event),
        color,
        cssClass: clsx(cssClass, {
          rounded: isTimeAvailability,
          '!border !border-yellow-400 !border-2 rounded': objectId === currentEventId,
          'is-filtered-out': (isFilteredOut || isCancelledOrNoShowEvent) && objectId !== currentEventId,
        }),
        ...(!isNil(recurrence) ? ({
          recurrence: {
            ...(recurrence ?? {}),
            ...(!isNil(recurrence?.endsOn)
              ? { until: recurrence?.endsOn }
              : {}
            ),
          },
        }) : {}),
        editable,
        serviceFacility,
      };
    },
  );
}
